/**
 * HVAC Template Definitions
 *
 * Pre-configured HVAC system templates covering common system types.
 */

import type { HVACTemplate, TemplateCategory } from './types';

/**
 * VAV with Hot Water Reheat Template
 */
const vavHotWaterReheat: HVACTemplate = {
  id: 'vav-hw-reheat',
  name: 'VAV with Hot Water Reheat',
  description: 'Variable air volume system with central AHU and hot water reheat coils at zone terminals. Ideal for large commercial buildings requiring precise zone control.',
  category: 'vav',
  applicability: {
    buildingTypes: ['office', 'retail', 'education', 'healthcare'],
    climateZones: ['3A', '4A', '5A', '6A', '4B', '5B'],
    sizeRange: { min: 2000, max: 50000 },
  },
  thumbnail: 'vav-reheat',
  requiresZones: true,
  minZones: 1,
  maxZones: 50,
  parameters: [
    {
      id: 'coolingType',
      name: 'Cooling Source',
      description: 'Type of cooling coil in the AHU',
      type: 'select',
      defaultValue: 'chilled-water',
      options: [
        { label: 'Chilled Water Coil', value: 'chilled-water' },
        { label: 'DX Cooling Coil', value: 'dx' },
      ],
      group: 'Cooling',
    },
    {
      id: 'supplyAirTemp',
      name: 'Supply Air Temperature',
      description: 'Design supply air temperature leaving the AHU',
      type: 'number',
      defaultValue: 12.8,
      unit: '°C',
      validation: { min: 10, max: 16, required: true },
      group: 'Air Handling',
    },
    {
      id: 'minOAPerPerson',
      name: 'Minimum OA per Person',
      description: 'Minimum outdoor air flow rate per person',
      type: 'number',
      defaultValue: 9.44,
      unit: 'L/s',
      validation: { min: 2.5, max: 25 },
      group: 'Ventilation',
    },
    {
      id: 'economizer',
      name: 'Include Economizer',
      description: 'Enable airside economizer for free cooling',
      type: 'boolean',
      defaultValue: true,
      group: 'Energy Recovery',
    },
    {
      id: 'heatRecovery',
      name: 'Include Heat Recovery',
      description: 'Add energy recovery ventilator',
      type: 'boolean',
      defaultValue: false,
      group: 'Energy Recovery',
    },
    {
      id: 'minAirFraction',
      name: 'Minimum Air Fraction',
      description: 'Minimum VAV box airflow as fraction of maximum',
      type: 'number',
      defaultValue: 0.3,
      validation: { min: 0.1, max: 0.5 },
      group: 'Zone Control',
    },
  ],
  components: [
    {
      id: 'oa-system',
      componentType: 'outdoor-air-system',
      namePattern: '{SystemName} OA System',
      position: { x: 100, y: 200 },
    },
    {
      id: 'heat-recovery',
      componentType: 'heat-exchanger-air-to-air',
      namePattern: '{SystemName} ERV',
      position: { x: 250, y: 200 },
      conditional: "parameters.heatRecovery === true",
    },
    {
      id: 'cooling-coil-cw',
      componentType: 'coil-cooling-water',
      namePattern: '{SystemName} Cooling Coil',
      position: { x: 400, y: 200 },
      conditional: "parameters.coolingType === 'chilled-water'",
    },
    {
      id: 'cooling-coil-dx',
      componentType: 'coil-cooling-dx-single-speed',
      namePattern: '{SystemName} DX Cooling Coil',
      position: { x: 400, y: 200 },
      conditional: "parameters.coolingType === 'dx'",
    },
    {
      id: 'heating-coil',
      componentType: 'coil-heating-water',
      namePattern: '{SystemName} Heating Coil',
      position: { x: 550, y: 200 },
    },
    {
      id: 'supply-fan',
      componentType: 'fan-variable-volume',
      namePattern: '{SystemName} Supply Fan',
      position: { x: 700, y: 200 },
    },
    {
      id: 'supply-splitter',
      componentType: 'supply-splitter',
      namePattern: '{SystemName} Supply Splitter',
      position: { x: 850, y: 200 },
    },
    {
      id: 'return-mixer',
      componentType: 'return-mixer',
      namePattern: '{SystemName} Return Mixer',
      position: { x: 850, y: 400 },
    },
  ],
  connections: [
    {
      sourceId: 'oa-system',
      sourcePort: 'mixed-air-outlet',
      targetId: 'heat-recovery',
      targetPort: 'supply-air-inlet',
      conditional: "parameters.heatRecovery === true",
    },
    {
      sourceId: 'heat-recovery',
      sourcePort: 'supply-air-outlet',
      targetId: 'cooling-coil-cw',
      targetPort: 'air-inlet',
      conditional: "parameters.heatRecovery === true && parameters.coolingType === 'chilled-water'",
    },
    {
      sourceId: 'oa-system',
      sourcePort: 'mixed-air-outlet',
      targetId: 'cooling-coil-cw',
      targetPort: 'air-inlet',
      conditional: "parameters.heatRecovery === false && parameters.coolingType === 'chilled-water'",
    },
    {
      sourceId: 'cooling-coil-cw',
      sourcePort: 'air-outlet',
      targetId: 'heating-coil',
      targetPort: 'air-inlet',
      conditional: "parameters.coolingType === 'chilled-water'",
    },
    {
      sourceId: 'oa-system',
      sourcePort: 'mixed-air-outlet',
      targetId: 'cooling-coil-dx',
      targetPort: 'air-inlet',
      conditional: "parameters.heatRecovery === false && parameters.coolingType === 'dx'",
    },
    {
      sourceId: 'heat-recovery',
      sourcePort: 'supply-air-outlet',
      targetId: 'cooling-coil-dx',
      targetPort: 'air-inlet',
      conditional: "parameters.heatRecovery === true && parameters.coolingType === 'dx'",
    },
    {
      sourceId: 'cooling-coil-dx',
      sourcePort: 'air-outlet',
      targetId: 'heating-coil',
      targetPort: 'air-inlet',
      conditional: "parameters.coolingType === 'dx'",
    },
    {
      sourceId: 'heating-coil',
      sourcePort: 'air-outlet',
      targetId: 'supply-fan',
      targetPort: 'air-inlet',
    },
    {
      sourceId: 'supply-fan',
      sourcePort: 'air-outlet',
      targetId: 'supply-splitter',
      targetPort: 'air-inlet',
    },
  ],
  controls: [
    {
      type: 'supply-air-temperature',
      parameters: {
        setpoint: 12.8,
        resetSchedule: 'OAT-based',
      },
    },
    {
      type: 'economizer',
      parameters: {
        type: 'DifferentialDryBulb',
        highLimit: 21,
      },
    },
  ],
  plantLoops: [
    {
      type: 'hot-water',
      equipment: 'boiler-hot-water',
      sizing: 'auto',
    },
    {
      type: 'chilled-water',
      equipment: 'chiller-electric',
      sizing: 'auto',
      conditional: "parameters.coolingType === 'chilled-water'",
    },
  ],
  terminalTypes: [
    {
      componentType: 'vav-box-reheat',
      hasReheat: true,
      fields: {
        'Zone Minimum Air Flow Input Method': 'Constant',
        'Constant Minimum Air Flow Fraction': '0.3',
      },
    },
  ],
};

/**
 * VAV with Electric Reheat Template
 */
const vavElectricReheat: HVACTemplate = {
  id: 'vav-electric-reheat',
  name: 'VAV with Electric Reheat',
  description: 'Variable air volume system with electric reheat coils at zone terminals. Suitable when hot water is not available.',
  category: 'vav',
  applicability: {
    buildingTypes: ['office', 'retail', 'education'],
    climateZones: ['2A', '2B', '3A', '3B', '3C', '4A', '4B', '4C'],
    sizeRange: { min: 1000, max: 30000 },
  },
  thumbnail: 'vav-electric',
  requiresZones: true,
  minZones: 1,
  maxZones: 40,
  parameters: [
    {
      id: 'coolingType',
      name: 'Cooling Source',
      description: 'Type of cooling coil in the AHU',
      type: 'select',
      defaultValue: 'dx',
      options: [
        { label: 'DX Cooling Coil', value: 'dx' },
        { label: 'Chilled Water Coil', value: 'chilled-water' },
      ],
      group: 'Cooling',
    },
    {
      id: 'supplyAirTemp',
      name: 'Supply Air Temperature',
      description: 'Design supply air temperature',
      type: 'number',
      defaultValue: 12.8,
      unit: '°C',
      validation: { min: 10, max: 16, required: true },
      group: 'Air Handling',
    },
    {
      id: 'economizer',
      name: 'Include Economizer',
      description: 'Enable airside economizer',
      type: 'boolean',
      defaultValue: true,
      group: 'Energy Recovery',
    },
    {
      id: 'minAirFraction',
      name: 'Minimum Air Fraction',
      description: 'Minimum VAV box airflow fraction',
      type: 'number',
      defaultValue: 0.3,
      validation: { min: 0.1, max: 0.5 },
      group: 'Zone Control',
    },
  ],
  components: [
    {
      id: 'oa-system',
      componentType: 'outdoor-air-system',
      namePattern: '{SystemName} OA System',
      position: { x: 100, y: 200 },
    },
    {
      id: 'cooling-coil-dx',
      componentType: 'coil-cooling-dx-two-speed',
      namePattern: '{SystemName} DX Cooling',
      position: { x: 300, y: 200 },
      conditional: "parameters.coolingType === 'dx'",
    },
    {
      id: 'cooling-coil-cw',
      componentType: 'coil-cooling-water',
      namePattern: '{SystemName} CHW Cooling',
      position: { x: 300, y: 200 },
      conditional: "parameters.coolingType === 'chilled-water'",
    },
    {
      id: 'supply-fan',
      componentType: 'fan-variable-volume',
      namePattern: '{SystemName} Supply Fan',
      position: { x: 500, y: 200 },
    },
    {
      id: 'supply-splitter',
      componentType: 'supply-splitter',
      namePattern: '{SystemName} Splitter',
      position: { x: 700, y: 200 },
    },
    {
      id: 'return-mixer',
      componentType: 'return-mixer',
      namePattern: '{SystemName} Mixer',
      position: { x: 700, y: 400 },
    },
  ],
  connections: [
    {
      sourceId: 'oa-system',
      sourcePort: 'mixed-air-outlet',
      targetId: 'cooling-coil-dx',
      targetPort: 'air-inlet',
      conditional: "parameters.coolingType === 'dx'",
    },
    {
      sourceId: 'oa-system',
      sourcePort: 'mixed-air-outlet',
      targetId: 'cooling-coil-cw',
      targetPort: 'air-inlet',
      conditional: "parameters.coolingType === 'chilled-water'",
    },
    {
      sourceId: 'cooling-coil-dx',
      sourcePort: 'air-outlet',
      targetId: 'supply-fan',
      targetPort: 'air-inlet',
      conditional: "parameters.coolingType === 'dx'",
    },
    {
      sourceId: 'cooling-coil-cw',
      sourcePort: 'air-outlet',
      targetId: 'supply-fan',
      targetPort: 'air-inlet',
      conditional: "parameters.coolingType === 'chilled-water'",
    },
    {
      sourceId: 'supply-fan',
      sourcePort: 'air-outlet',
      targetId: 'supply-splitter',
      targetPort: 'air-inlet',
    },
  ],
  controls: [
    {
      type: 'supply-air-temperature',
      parameters: { setpoint: 12.8 },
    },
  ],
  plantLoops: [
    {
      type: 'chilled-water',
      equipment: 'chiller-electric',
      sizing: 'auto',
      conditional: "parameters.coolingType === 'chilled-water'",
    },
  ],
  terminalTypes: [
    {
      componentType: 'vav-box-reheat',
      hasReheat: true,
      fields: {
        'Reheat Coil Object Type': 'Coil:Heating:Electric',
      },
    },
  ],
};

/**
 * VAV Cooling Only (No Reheat) Template
 */
const vavNoReheat: HVACTemplate = {
  id: 'vav-no-reheat',
  name: 'VAV Cooling Only',
  description: 'Variable air volume system with cooling only. No zone reheat - suitable for cooling-dominated climates.',
  category: 'vav',
  applicability: {
    buildingTypes: ['office', 'retail', 'warehouse'],
    climateZones: ['1A', '1B', '2A', '2B', '3A', '3B'],
    sizeRange: { min: 500, max: 20000 },
  },
  thumbnail: 'vav-cooling',
  requiresZones: true,
  minZones: 1,
  maxZones: 30,
  parameters: [
    {
      id: 'coolingType',
      name: 'Cooling Source',
      type: 'select',
      description: 'Type of cooling',
      defaultValue: 'dx',
      options: [
        { label: 'DX Cooling', value: 'dx' },
        { label: 'Chilled Water', value: 'chilled-water' },
      ],
    },
    {
      id: 'supplyAirTemp',
      name: 'Supply Air Temperature',
      type: 'number',
      description: 'Design supply air temperature',
      defaultValue: 13.3,
      unit: '°C',
      validation: { min: 10, max: 18 },
    },
    {
      id: 'economizer',
      name: 'Include Economizer',
      type: 'boolean',
      description: 'Enable economizer',
      defaultValue: true,
    },
  ],
  components: [
    {
      id: 'oa-system',
      componentType: 'outdoor-air-system',
      namePattern: '{SystemName} OA',
      position: { x: 100, y: 200 },
    },
    {
      id: 'cooling-coil',
      componentType: 'coil-cooling-dx-single-speed',
      namePattern: '{SystemName} Cooling',
      position: { x: 300, y: 200 },
    },
    {
      id: 'supply-fan',
      componentType: 'fan-variable-volume',
      namePattern: '{SystemName} Fan',
      position: { x: 500, y: 200 },
    },
    {
      id: 'splitter',
      componentType: 'supply-splitter',
      namePattern: '{SystemName} Splitter',
      position: { x: 700, y: 200 },
    },
    {
      id: 'mixer',
      componentType: 'return-mixer',
      namePattern: '{SystemName} Mixer',
      position: { x: 700, y: 400 },
    },
  ],
  connections: [
    { sourceId: 'oa-system', sourcePort: 'mixed-air-outlet', targetId: 'cooling-coil', targetPort: 'air-inlet' },
    { sourceId: 'cooling-coil', sourcePort: 'air-outlet', targetId: 'supply-fan', targetPort: 'air-inlet' },
    { sourceId: 'supply-fan', sourcePort: 'air-outlet', targetId: 'splitter', targetPort: 'air-inlet' },
  ],
  controls: [],
  plantLoops: [],
  terminalTypes: [
    { componentType: 'vav-box-no-reheat', hasReheat: false },
  ],
};

/**
 * Packaged Single Zone (PSZ-AC) Template
 */
const pszAC: HVACTemplate = {
  id: 'psz-ac',
  name: 'Packaged Single Zone (PSZ-AC)',
  description: 'Packaged rooftop unit with DX cooling and gas heating. One system serves one zone. Common for small commercial buildings.',
  category: 'packaged',
  applicability: {
    buildingTypes: ['retail', 'restaurant', 'warehouse', 'office'],
    climateZones: ['2A', '2B', '3A', '3B', '3C', '4A', '4B', '4C', '5A', '5B'],
    sizeRange: { min: 100, max: 3000 },
  },
  thumbnail: 'psz-ac',
  requiresZones: true,
  minZones: 1,
  maxZones: 1,
  parameters: [
    {
      id: 'coolingCapacity',
      name: 'Cooling Capacity',
      type: 'select',
      description: 'Nominal cooling capacity',
      defaultValue: 'autosize',
      options: [
        { label: 'Autosize', value: 'autosize' },
        { label: '5 ton (17.6 kW)', value: '17600' },
        { label: '7.5 ton (26.4 kW)', value: '26400' },
        { label: '10 ton (35.2 kW)', value: '35200' },
        { label: '15 ton (52.8 kW)', value: '52800' },
        { label: '20 ton (70.3 kW)', value: '70300' },
      ],
    },
    {
      id: 'heatingType',
      name: 'Heating Type',
      type: 'select',
      description: 'Type of heating',
      defaultValue: 'gas',
      options: [
        { label: 'Gas Furnace', value: 'gas' },
        { label: 'Electric Heat', value: 'electric' },
        { label: 'Heat Pump', value: 'heatpump' },
      ],
    },
    {
      id: 'economizer',
      name: 'Include Economizer',
      type: 'boolean',
      description: 'Enable economizer',
      defaultValue: true,
    },
    {
      id: 'supplyFanType',
      name: 'Supply Fan Type',
      type: 'select',
      description: 'Fan operation mode',
      defaultValue: 'constant',
      options: [
        { label: 'Constant Volume', value: 'constant' },
        { label: 'Cycling', value: 'cycling' },
      ],
    },
  ],
  components: [
    {
      id: 'oa-system',
      componentType: 'outdoor-air-system',
      namePattern: '{SystemName} OA',
      position: { x: 100, y: 200 },
    },
    {
      id: 'cooling-coil',
      componentType: 'coil-cooling-dx-single-speed',
      namePattern: '{SystemName} DX Cooling',
      position: { x: 300, y: 200 },
    },
    {
      id: 'heating-coil-gas',
      componentType: 'coil-heating-gas',
      namePattern: '{SystemName} Gas Heat',
      position: { x: 500, y: 200 },
      conditional: "parameters.heatingType === 'gas'",
    },
    {
      id: 'heating-coil-electric',
      componentType: 'coil-heating-electric',
      namePattern: '{SystemName} Electric Heat',
      position: { x: 500, y: 200 },
      conditional: "parameters.heatingType === 'electric'",
    },
    {
      id: 'heating-coil-hp',
      componentType: 'coil-heating-dx-single-speed',
      namePattern: '{SystemName} HP Heating',
      position: { x: 500, y: 200 },
      conditional: "parameters.heatingType === 'heatpump'",
    },
    {
      id: 'supply-fan-constant',
      componentType: 'fan-constant-volume',
      namePattern: '{SystemName} Fan',
      position: { x: 700, y: 200 },
      conditional: "parameters.supplyFanType === 'constant'",
    },
    {
      id: 'supply-fan-cycling',
      componentType: 'fan-on-off',
      namePattern: '{SystemName} Fan',
      position: { x: 700, y: 200 },
      conditional: "parameters.supplyFanType === 'cycling'",
    },
    {
      id: 'diffuser',
      componentType: 'diffuser',
      namePattern: '{SystemName} Diffuser',
      position: { x: 900, y: 200 },
    },
  ],
  connections: [
    { sourceId: 'oa-system', sourcePort: 'mixed-air-outlet', targetId: 'cooling-coil', targetPort: 'air-inlet' },
    { sourceId: 'cooling-coil', sourcePort: 'air-outlet', targetId: 'heating-coil-gas', targetPort: 'air-inlet', conditional: "parameters.heatingType === 'gas'" },
    { sourceId: 'cooling-coil', sourcePort: 'air-outlet', targetId: 'heating-coil-electric', targetPort: 'air-inlet', conditional: "parameters.heatingType === 'electric'" },
    { sourceId: 'cooling-coil', sourcePort: 'air-outlet', targetId: 'heating-coil-hp', targetPort: 'air-inlet', conditional: "parameters.heatingType === 'heatpump'" },
    { sourceId: 'heating-coil-gas', sourcePort: 'air-outlet', targetId: 'supply-fan-constant', targetPort: 'air-inlet', conditional: "parameters.heatingType === 'gas' && parameters.supplyFanType === 'constant'" },
    { sourceId: 'heating-coil-gas', sourcePort: 'air-outlet', targetId: 'supply-fan-cycling', targetPort: 'air-inlet', conditional: "parameters.heatingType === 'gas' && parameters.supplyFanType === 'cycling'" },
    { sourceId: 'heating-coil-electric', sourcePort: 'air-outlet', targetId: 'supply-fan-constant', targetPort: 'air-inlet', conditional: "parameters.heatingType === 'electric' && parameters.supplyFanType === 'constant'" },
    { sourceId: 'heating-coil-electric', sourcePort: 'air-outlet', targetId: 'supply-fan-cycling', targetPort: 'air-inlet', conditional: "parameters.heatingType === 'electric' && parameters.supplyFanType === 'cycling'" },
    { sourceId: 'heating-coil-hp', sourcePort: 'air-outlet', targetId: 'supply-fan-constant', targetPort: 'air-inlet', conditional: "parameters.heatingType === 'heatpump' && parameters.supplyFanType === 'constant'" },
    { sourceId: 'heating-coil-hp', sourcePort: 'air-outlet', targetId: 'supply-fan-cycling', targetPort: 'air-inlet', conditional: "parameters.heatingType === 'heatpump' && parameters.supplyFanType === 'cycling'" },
    { sourceId: 'supply-fan-constant', sourcePort: 'air-outlet', targetId: 'diffuser', targetPort: 'air-inlet', conditional: "parameters.supplyFanType === 'constant'" },
    { sourceId: 'supply-fan-cycling', sourcePort: 'air-outlet', targetId: 'diffuser', targetPort: 'air-inlet', conditional: "parameters.supplyFanType === 'cycling'" },
  ],
  controls: [
    { type: 'thermostat', parameters: { coolingSetpoint: 24, heatingSetpoint: 21 } },
  ],
  plantLoops: [],
  terminalTypes: [],
};

/**
 * Packaged VAV (PVAV) Template
 */
const pvav: HVACTemplate = {
  id: 'pvav',
  name: 'Packaged VAV (PVAV)',
  description: 'Packaged rooftop VAV unit with DX cooling and gas heating. Multiple zones with VAV terminals.',
  category: 'packaged',
  applicability: {
    buildingTypes: ['office', 'retail', 'education'],
    climateZones: ['3A', '3B', '4A', '4B', '5A', '5B'],
    sizeRange: { min: 1000, max: 15000 },
  },
  thumbnail: 'pvav',
  requiresZones: true,
  minZones: 2,
  maxZones: 20,
  parameters: [
    {
      id: 'coolingStages',
      name: 'Cooling Stages',
      type: 'select',
      description: 'Number of DX cooling stages',
      defaultValue: '2',
      options: [
        { label: 'Single Stage', value: '1' },
        { label: 'Two Stage', value: '2' },
      ],
    },
    {
      id: 'heatingType',
      name: 'Heating Type',
      type: 'select',
      description: 'Type of central heating',
      defaultValue: 'gas',
      options: [
        { label: 'Gas Furnace', value: 'gas' },
        { label: 'Electric', value: 'electric' },
      ],
    },
    {
      id: 'reheatType',
      name: 'Zone Reheat Type',
      type: 'select',
      description: 'Type of zone reheat',
      defaultValue: 'electric',
      options: [
        { label: 'Electric Reheat', value: 'electric' },
        { label: 'Hot Water Reheat', value: 'hot-water' },
        { label: 'No Reheat', value: 'none' },
      ],
    },
    {
      id: 'economizer',
      name: 'Include Economizer',
      type: 'boolean',
      description: 'Enable economizer',
      defaultValue: true,
    },
  ],
  components: [
    {
      id: 'oa-system',
      componentType: 'outdoor-air-system',
      namePattern: '{SystemName} OA',
      position: { x: 100, y: 200 },
    },
    {
      id: 'dx-cooling',
      componentType: 'coil-cooling-dx-two-speed',
      namePattern: '{SystemName} DX Cooling',
      position: { x: 300, y: 200 },
    },
    {
      id: 'gas-heating',
      componentType: 'coil-heating-gas',
      namePattern: '{SystemName} Gas Heat',
      position: { x: 500, y: 200 },
      conditional: "parameters.heatingType === 'gas'",
    },
    {
      id: 'electric-heating',
      componentType: 'coil-heating-electric',
      namePattern: '{SystemName} Electric Heat',
      position: { x: 500, y: 200 },
      conditional: "parameters.heatingType === 'electric'",
    },
    {
      id: 'supply-fan',
      componentType: 'fan-variable-volume',
      namePattern: '{SystemName} Supply Fan',
      position: { x: 700, y: 200 },
    },
    {
      id: 'splitter',
      componentType: 'supply-splitter',
      namePattern: '{SystemName} Splitter',
      position: { x: 900, y: 200 },
    },
    {
      id: 'mixer',
      componentType: 'return-mixer',
      namePattern: '{SystemName} Mixer',
      position: { x: 900, y: 400 },
    },
  ],
  connections: [
    { sourceId: 'oa-system', sourcePort: 'mixed-air-outlet', targetId: 'dx-cooling', targetPort: 'air-inlet' },
    { sourceId: 'dx-cooling', sourcePort: 'air-outlet', targetId: 'gas-heating', targetPort: 'air-inlet', conditional: "parameters.heatingType === 'gas'" },
    { sourceId: 'dx-cooling', sourcePort: 'air-outlet', targetId: 'electric-heating', targetPort: 'air-inlet', conditional: "parameters.heatingType === 'electric'" },
    { sourceId: 'gas-heating', sourcePort: 'air-outlet', targetId: 'supply-fan', targetPort: 'air-inlet', conditional: "parameters.heatingType === 'gas'" },
    { sourceId: 'electric-heating', sourcePort: 'air-outlet', targetId: 'supply-fan', targetPort: 'air-inlet', conditional: "parameters.heatingType === 'electric'" },
    { sourceId: 'supply-fan', sourcePort: 'air-outlet', targetId: 'splitter', targetPort: 'air-inlet' },
  ],
  controls: [],
  plantLoops: [
    {
      type: 'hot-water',
      equipment: 'boiler-hot-water',
      sizing: 'auto',
      conditional: "parameters.reheatType === 'hot-water'",
    },
  ],
  terminalTypes: [
    { componentType: 'vav-box-reheat', hasReheat: true },
    { componentType: 'vav-box-no-reheat', hasReheat: false },
  ],
};

/**
 * Packaged Rooftop with Gas Heat Template
 */
const rooftopGas: HVACTemplate = {
  id: 'rooftop-gas',
  name: 'Rooftop Unit with Gas Heat',
  description: 'Packaged rooftop unit with DX cooling and gas furnace. Constant volume, single zone application.',
  category: 'packaged',
  applicability: {
    buildingTypes: ['retail', 'restaurant', 'warehouse', 'office'],
    climateZones: ['3A', '4A', '5A', '6A', '4B', '5B', '6B'],
    sizeRange: { min: 200, max: 5000 },
  },
  thumbnail: 'rooftop-gas',
  requiresZones: true,
  minZones: 1,
  maxZones: 1,
  parameters: [
    {
      id: 'coolingCOP',
      name: 'Cooling COP',
      type: 'number',
      description: 'Cooling coefficient of performance',
      defaultValue: 3.5,
      validation: { min: 2.5, max: 5.0 },
    },
    {
      id: 'heatingEfficiency',
      name: 'Heating Efficiency',
      type: 'number',
      description: 'Gas heating efficiency',
      defaultValue: 0.8,
      validation: { min: 0.7, max: 0.98 },
    },
    {
      id: 'economizer',
      name: 'Include Economizer',
      type: 'boolean',
      description: 'Enable economizer',
      defaultValue: true,
    },
  ],
  components: [
    {
      id: 'oa-system',
      componentType: 'outdoor-air-system',
      namePattern: '{SystemName} OA',
      position: { x: 100, y: 200 },
    },
    {
      id: 'cooling-coil',
      componentType: 'coil-cooling-dx-single-speed',
      namePattern: '{SystemName} Cooling',
      position: { x: 300, y: 200 },
    },
    {
      id: 'heating-coil',
      componentType: 'coil-heating-gas',
      namePattern: '{SystemName} Gas Furnace',
      position: { x: 500, y: 200 },
    },
    {
      id: 'supply-fan',
      componentType: 'fan-constant-volume',
      namePattern: '{SystemName} Fan',
      position: { x: 700, y: 200 },
    },
    {
      id: 'diffuser',
      componentType: 'diffuser',
      namePattern: '{SystemName} Diffuser',
      position: { x: 900, y: 200 },
    },
  ],
  connections: [
    { sourceId: 'oa-system', sourcePort: 'mixed-air-outlet', targetId: 'cooling-coil', targetPort: 'air-inlet' },
    { sourceId: 'cooling-coil', sourcePort: 'air-outlet', targetId: 'heating-coil', targetPort: 'air-inlet' },
    { sourceId: 'heating-coil', sourcePort: 'air-outlet', targetId: 'supply-fan', targetPort: 'air-inlet' },
    { sourceId: 'supply-fan', sourcePort: 'air-outlet', targetId: 'diffuser', targetPort: 'air-inlet' },
  ],
  controls: [],
  plantLoops: [],
  terminalTypes: [],
};

/**
 * VRF Heat Pump Template
 */
const vrfHeatPump: HVACTemplate = {
  id: 'vrf-heat-pump',
  name: 'VRF Heat Pump System',
  description: 'Variable Refrigerant Flow heat pump system with multiple indoor units. High efficiency, individual zone control.',
  category: 'vrf',
  applicability: {
    buildingTypes: ['office', 'hotel', 'residential', 'education'],
    climateZones: ['2A', '3A', '4A', '5A', '3B', '4B', '4C'],
    sizeRange: { min: 500, max: 20000 },
  },
  thumbnail: 'vrf-hp',
  requiresZones: true,
  minZones: 2,
  maxZones: 30,
  parameters: [
    {
      id: 'outdoorUnitCapacity',
      name: 'Outdoor Unit Capacity',
      type: 'select',
      description: 'Total outdoor unit cooling capacity',
      defaultValue: 'autosize',
      options: [
        { label: 'Autosize', value: 'autosize' },
        { label: '8 ton (28 kW)', value: '28000' },
        { label: '12 ton (42 kW)', value: '42000' },
        { label: '16 ton (56 kW)', value: '56000' },
        { label: '20 ton (70 kW)', value: '70000' },
      ],
    },
    {
      id: 'heatRecovery',
      name: 'Heat Recovery',
      type: 'boolean',
      description: 'Enable simultaneous heating and cooling (3-pipe)',
      defaultValue: false,
    },
    {
      id: 'defrostStrategy',
      name: 'Defrost Strategy',
      type: 'select',
      description: 'Defrost control method',
      defaultValue: 'ReverseCycle',
      options: [
        { label: 'Reverse Cycle', value: 'ReverseCycle' },
        { label: 'Resistive', value: 'Resistive' },
      ],
    },
    {
      id: 'minOAMethod',
      name: 'Ventilation Method',
      type: 'select',
      description: 'How outdoor air is provided',
      defaultValue: 'none',
      options: [
        { label: 'None (Natural Ventilation)', value: 'none' },
        { label: 'Separate DOAS', value: 'doas' },
        { label: 'Individual ERVs', value: 'erv' },
      ],
    },
  ],
  components: [
    // VRF systems are modeled differently - primarily through zone equipment
    // The template engine will create ZoneHVAC:TerminalUnit:VariableRefrigerantFlow objects
  ],
  connections: [],
  controls: [
    { type: 'vrf-controller', parameters: { masterThermostat: 'zone-1' } },
  ],
  plantLoops: [],
  terminalTypes: [
    { componentType: 'vrf-terminal', hasReheat: false },
  ],
};

/**
 * VRF with DOAS Template
 */
const vrfDOAS: HVACTemplate = {
  id: 'vrf-doas',
  name: 'VRF with DOAS',
  description: 'Variable Refrigerant Flow system with Dedicated Outdoor Air System for ventilation. Decoupled ventilation and space conditioning.',
  category: 'vrf',
  applicability: {
    buildingTypes: ['office', 'hotel', 'education', 'healthcare'],
    climateZones: ['3A', '4A', '5A', '6A', '3B', '4B', '5B'],
    sizeRange: { min: 1000, max: 30000 },
  },
  thumbnail: 'vrf-doas',
  requiresZones: true,
  minZones: 2,
  maxZones: 40,
  parameters: [
    {
      id: 'doasCooling',
      name: 'DOAS Cooling Type',
      type: 'select',
      description: 'Cooling source for DOAS',
      defaultValue: 'dx',
      options: [
        { label: 'DX Cooling', value: 'dx' },
        { label: 'Chilled Water', value: 'chw' },
      ],
    },
    {
      id: 'doasHeating',
      name: 'DOAS Heating Type',
      type: 'select',
      description: 'Heating source for DOAS',
      defaultValue: 'gas',
      options: [
        { label: 'Gas', value: 'gas' },
        { label: 'Electric', value: 'electric' },
        { label: 'Hot Water', value: 'hw' },
      ],
    },
    {
      id: 'energyRecovery',
      name: 'Energy Recovery',
      type: 'boolean',
      description: 'Include energy recovery on DOAS',
      defaultValue: true,
    },
    {
      id: 'supplyAirTemp',
      name: 'DOAS Supply Temperature',
      type: 'number',
      description: 'Neutral supply air temperature',
      defaultValue: 18,
      unit: '°C',
      validation: { min: 15, max: 22 },
    },
  ],
  components: [
    // DOAS components
    {
      id: 'erv',
      componentType: 'heat-exchanger-air-to-air',
      namePattern: '{SystemName} DOAS ERV',
      position: { x: 100, y: 100 },
      conditional: "parameters.energyRecovery === true",
    },
    {
      id: 'doas-cooling-dx',
      componentType: 'coil-cooling-dx-single-speed',
      namePattern: '{SystemName} DOAS Cooling',
      position: { x: 300, y: 100 },
      conditional: "parameters.doasCooling === 'dx'",
    },
    {
      id: 'doas-cooling-cw',
      componentType: 'coil-cooling-water',
      namePattern: '{SystemName} DOAS CHW Coil',
      position: { x: 300, y: 100 },
      conditional: "parameters.doasCooling === 'chw'",
    },
    {
      id: 'doas-heating-gas',
      componentType: 'coil-heating-gas',
      namePattern: '{SystemName} DOAS Gas Heat',
      position: { x: 500, y: 100 },
      conditional: "parameters.doasHeating === 'gas'",
    },
    {
      id: 'doas-heating-electric',
      componentType: 'coil-heating-electric',
      namePattern: '{SystemName} DOAS Electric Heat',
      position: { x: 500, y: 100 },
      conditional: "parameters.doasHeating === 'electric'",
    },
    {
      id: 'doas-heating-hw',
      componentType: 'coil-heating-water',
      namePattern: '{SystemName} DOAS HW Coil',
      position: { x: 500, y: 100 },
      conditional: "parameters.doasHeating === 'hw'",
    },
    {
      id: 'doas-fan',
      componentType: 'fan-constant-volume',
      namePattern: '{SystemName} DOAS Fan',
      position: { x: 700, y: 100 },
    },
  ],
  connections: [
    { sourceId: 'erv', sourcePort: 'supply-air-outlet', targetId: 'doas-cooling-dx', targetPort: 'air-inlet', conditional: "parameters.energyRecovery === true && parameters.doasCooling === 'dx'" },
    { sourceId: 'erv', sourcePort: 'supply-air-outlet', targetId: 'doas-cooling-cw', targetPort: 'air-inlet', conditional: "parameters.energyRecovery === true && parameters.doasCooling === 'chw'" },
    { sourceId: 'doas-cooling-dx', sourcePort: 'air-outlet', targetId: 'doas-heating-gas', targetPort: 'air-inlet', conditional: "parameters.doasCooling === 'dx' && parameters.doasHeating === 'gas'" },
    { sourceId: 'doas-cooling-dx', sourcePort: 'air-outlet', targetId: 'doas-heating-electric', targetPort: 'air-inlet', conditional: "parameters.doasCooling === 'dx' && parameters.doasHeating === 'electric'" },
    { sourceId: 'doas-cooling-dx', sourcePort: 'air-outlet', targetId: 'doas-heating-hw', targetPort: 'air-inlet', conditional: "parameters.doasCooling === 'dx' && parameters.doasHeating === 'hw'" },
    { sourceId: 'doas-cooling-cw', sourcePort: 'air-outlet', targetId: 'doas-heating-gas', targetPort: 'air-inlet', conditional: "parameters.doasCooling === 'chw' && parameters.doasHeating === 'gas'" },
    { sourceId: 'doas-cooling-cw', sourcePort: 'air-outlet', targetId: 'doas-heating-electric', targetPort: 'air-inlet', conditional: "parameters.doasCooling === 'chw' && parameters.doasHeating === 'electric'" },
    { sourceId: 'doas-cooling-cw', sourcePort: 'air-outlet', targetId: 'doas-heating-hw', targetPort: 'air-inlet', conditional: "parameters.doasCooling === 'chw' && parameters.doasHeating === 'hw'" },
    { sourceId: 'doas-heating-gas', sourcePort: 'air-outlet', targetId: 'doas-fan', targetPort: 'air-inlet', conditional: "parameters.doasHeating === 'gas'" },
    { sourceId: 'doas-heating-electric', sourcePort: 'air-outlet', targetId: 'doas-fan', targetPort: 'air-inlet', conditional: "parameters.doasHeating === 'electric'" },
    { sourceId: 'doas-heating-hw', sourcePort: 'air-outlet', targetId: 'doas-fan', targetPort: 'air-inlet', conditional: "parameters.doasHeating === 'hw'" },
  ],
  controls: [],
  plantLoops: [
    {
      type: 'chilled-water',
      equipment: 'chiller-electric',
      sizing: 'auto',
      conditional: "parameters.doasCooling === 'chw'",
    },
    {
      type: 'hot-water',
      equipment: 'boiler-hot-water',
      sizing: 'auto',
      conditional: "parameters.doasHeating === 'hw'",
    },
  ],
  terminalTypes: [
    { componentType: 'vrf-terminal', hasReheat: false },
  ],
};

/**
 * DOAS + Radiant Floor Template
 */
const doasRadiantFloor: HVACTemplate = {
  id: 'doas-radiant-floor',
  name: 'DOAS + Radiant Floor',
  description: 'Dedicated outdoor air system combined with radiant floor heating and cooling. High comfort, low energy use.',
  category: 'radiant',
  applicability: {
    buildingTypes: ['office', 'education', 'healthcare', 'residential'],
    climateZones: ['4A', '5A', '6A', '7', '4B', '5B', '6B'],
    sizeRange: { min: 500, max: 25000 },
  },
  thumbnail: 'doas-radiant',
  requiresZones: true,
  minZones: 1,
  maxZones: 30,
  parameters: [
    {
      id: 'radiantType',
      name: 'Radiant System Type',
      type: 'select',
      description: 'Type of radiant system',
      defaultValue: 'floor-heating-cooling',
      options: [
        { label: 'Floor Heating & Cooling', value: 'floor-heating-cooling' },
        { label: 'Floor Heating Only', value: 'floor-heating' },
        { label: 'Ceiling Cooling', value: 'ceiling-cooling' },
      ],
    },
    {
      id: 'doasCooling',
      name: 'DOAS Cooling',
      type: 'select',
      description: 'DOAS cooling method',
      defaultValue: 'dx',
      options: [
        { label: 'DX Coil', value: 'dx' },
        { label: 'Chilled Water', value: 'chw' },
      ],
    },
    {
      id: 'energyRecovery',
      name: 'Energy Recovery',
      type: 'boolean',
      description: 'Include heat recovery',
      defaultValue: true,
    },
    {
      id: 'condensateControl',
      name: 'Condensate Control',
      type: 'select',
      description: 'Condensation prevention strategy',
      defaultValue: 'dewpoint',
      options: [
        { label: 'Dewpoint Tracking', value: 'dewpoint' },
        { label: 'Fixed Offset', value: 'fixed' },
      ],
    },
  ],
  components: [
    {
      id: 'erv',
      componentType: 'heat-exchanger-air-to-air',
      namePattern: '{SystemName} ERV',
      position: { x: 100, y: 200 },
      conditional: "parameters.energyRecovery === true",
    },
    {
      id: 'doas-cooling',
      componentType: 'coil-cooling-dx-single-speed',
      namePattern: '{SystemName} DOAS Cooling',
      position: { x: 300, y: 200 },
    },
    {
      id: 'doas-heating',
      componentType: 'coil-heating-water',
      namePattern: '{SystemName} DOAS Heating',
      position: { x: 500, y: 200 },
    },
    {
      id: 'doas-fan',
      componentType: 'fan-constant-volume',
      namePattern: '{SystemName} DOAS Fan',
      position: { x: 700, y: 200 },
    },
  ],
  connections: [
    { sourceId: 'erv', sourcePort: 'supply-air-outlet', targetId: 'doas-cooling', targetPort: 'air-inlet', conditional: "parameters.energyRecovery === true" },
    { sourceId: 'doas-cooling', sourcePort: 'air-outlet', targetId: 'doas-heating', targetPort: 'air-inlet' },
    { sourceId: 'doas-heating', sourcePort: 'air-outlet', targetId: 'doas-fan', targetPort: 'air-inlet' },
  ],
  controls: [
    { type: 'radiant-slab', parameters: { controlType: 'OutdoorAirTemperatureReset' } },
  ],
  plantLoops: [
    {
      type: 'hot-water',
      equipment: 'boiler-hot-water',
      sizing: 'auto',
      namePattern: '{SystemName} HW Loop',
    },
    {
      type: 'chilled-water',
      equipment: 'chiller-electric',
      sizing: 'auto',
      namePattern: '{SystemName} CHW Loop',
      conditional: "parameters.radiantType !== 'floor-heating'",
    },
  ],
  terminalTypes: [],
};

/**
 * Residential Split System Template
 */
const residentialSplit: HVACTemplate = {
  id: 'residential-split',
  name: 'Residential Split System',
  description: 'Typical residential system with gas furnace and split-system air conditioning. Single zone.',
  category: 'residential',
  applicability: {
    buildingTypes: ['residential'],
    climateZones: ['2A', '3A', '4A', '5A', '6A', '3B', '4B', '5B'],
    sizeRange: { min: 50, max: 500 },
  },
  thumbnail: 'residential-split',
  requiresZones: true,
  minZones: 1,
  maxZones: 1,
  parameters: [
    {
      id: 'coolingCapacity',
      name: 'Cooling Capacity',
      type: 'select',
      description: 'Air conditioner capacity',
      defaultValue: 'autosize',
      options: [
        { label: 'Autosize', value: 'autosize' },
        { label: '1.5 ton (5.3 kW)', value: '5300' },
        { label: '2 ton (7 kW)', value: '7000' },
        { label: '2.5 ton (8.8 kW)', value: '8800' },
        { label: '3 ton (10.5 kW)', value: '10500' },
        { label: '3.5 ton (12.3 kW)', value: '12300' },
        { label: '4 ton (14 kW)', value: '14000' },
        { label: '5 ton (17.6 kW)', value: '17600' },
      ],
    },
    {
      id: 'furnaceCapacity',
      name: 'Furnace Capacity',
      type: 'select',
      description: 'Gas furnace capacity',
      defaultValue: 'autosize',
      options: [
        { label: 'Autosize', value: 'autosize' },
        { label: '40,000 BTU/h (11.7 kW)', value: '11700' },
        { label: '60,000 BTU/h (17.6 kW)', value: '17600' },
        { label: '80,000 BTU/h (23.4 kW)', value: '23400' },
        { label: '100,000 BTU/h (29.3 kW)', value: '29300' },
        { label: '120,000 BTU/h (35.2 kW)', value: '35200' },
      ],
    },
    {
      id: 'furnaceEfficiency',
      name: 'Furnace Efficiency',
      type: 'select',
      description: 'AFUE rating',
      defaultValue: '0.80',
      options: [
        { label: '80% AFUE', value: '0.80' },
        { label: '92% AFUE', value: '0.92' },
        { label: '95% AFUE', value: '0.95' },
        { label: '98% AFUE', value: '0.98' },
      ],
    },
    {
      id: 'seer',
      name: 'SEER Rating',
      type: 'select',
      description: 'Air conditioner efficiency',
      defaultValue: '14',
      options: [
        { label: 'SEER 13', value: '13' },
        { label: 'SEER 14', value: '14' },
        { label: 'SEER 16', value: '16' },
        { label: 'SEER 18', value: '18' },
        { label: 'SEER 20+', value: '20' },
      ],
    },
  ],
  components: [
    {
      id: 'cooling-coil',
      componentType: 'coil-cooling-dx-single-speed',
      namePattern: '{SystemName} AC',
      position: { x: 100, y: 200 },
    },
    {
      id: 'furnace',
      componentType: 'coil-heating-gas',
      namePattern: '{SystemName} Furnace',
      position: { x: 300, y: 200 },
    },
    {
      id: 'fan',
      componentType: 'fan-on-off',
      namePattern: '{SystemName} Blower',
      position: { x: 500, y: 200 },
    },
    {
      id: 'diffuser',
      componentType: 'diffuser',
      namePattern: '{SystemName} Supply',
      position: { x: 700, y: 200 },
    },
  ],
  connections: [
    { sourceId: 'cooling-coil', sourcePort: 'air-outlet', targetId: 'furnace', targetPort: 'air-inlet' },
    { sourceId: 'furnace', sourcePort: 'air-outlet', targetId: 'fan', targetPort: 'air-inlet' },
    { sourceId: 'fan', sourcePort: 'air-outlet', targetId: 'diffuser', targetPort: 'air-inlet' },
  ],
  controls: [],
  plantLoops: [],
  terminalTypes: [],
};

/**
 * Residential Heat Pump Template
 */
const residentialHeatPump: HVACTemplate = {
  id: 'residential-heat-pump',
  name: 'Residential Heat Pump',
  description: 'All-electric residential heat pump for heating and cooling. Single zone, no combustion.',
  category: 'residential',
  applicability: {
    buildingTypes: ['residential'],
    climateZones: ['2A', '3A', '4A', '2B', '3B', '3C', '4B', '4C'],
    sizeRange: { min: 50, max: 400 },
  },
  thumbnail: 'residential-hp',
  requiresZones: true,
  minZones: 1,
  maxZones: 1,
  parameters: [
    {
      id: 'capacity',
      name: 'Heat Pump Capacity',
      type: 'select',
      description: 'Nominal capacity',
      defaultValue: 'autosize',
      options: [
        { label: 'Autosize', value: 'autosize' },
        { label: '1.5 ton', value: '5300' },
        { label: '2 ton', value: '7000' },
        { label: '2.5 ton', value: '8800' },
        { label: '3 ton', value: '10500' },
        { label: '3.5 ton', value: '12300' },
        { label: '4 ton', value: '14000' },
        { label: '5 ton', value: '17600' },
      ],
    },
    {
      id: 'hspf',
      name: 'HSPF Rating',
      type: 'select',
      description: 'Heating efficiency',
      defaultValue: '8.5',
      options: [
        { label: 'HSPF 8', value: '8' },
        { label: 'HSPF 8.5', value: '8.5' },
        { label: 'HSPF 9', value: '9' },
        { label: 'HSPF 10', value: '10' },
        { label: 'HSPF 11+', value: '11' },
      ],
    },
    {
      id: 'seer',
      name: 'SEER Rating',
      type: 'select',
      description: 'Cooling efficiency',
      defaultValue: '15',
      options: [
        { label: 'SEER 14', value: '14' },
        { label: 'SEER 15', value: '15' },
        { label: 'SEER 16', value: '16' },
        { label: 'SEER 18', value: '18' },
        { label: 'SEER 20+', value: '20' },
      ],
    },
    {
      id: 'auxiliaryHeat',
      name: 'Auxiliary Heat',
      type: 'boolean',
      description: 'Include electric backup heat strips',
      defaultValue: true,
    },
  ],
  components: [
    {
      id: 'cooling-coil',
      componentType: 'coil-cooling-dx-single-speed',
      namePattern: '{SystemName} Cooling',
      position: { x: 100, y: 200 },
    },
    {
      id: 'heating-coil',
      componentType: 'coil-heating-dx-single-speed',
      namePattern: '{SystemName} HP Heating',
      position: { x: 300, y: 200 },
    },
    {
      id: 'aux-heat',
      componentType: 'coil-heating-electric',
      namePattern: '{SystemName} Aux Heat',
      position: { x: 500, y: 200 },
      conditional: "parameters.auxiliaryHeat === true",
    },
    {
      id: 'fan',
      componentType: 'fan-on-off',
      namePattern: '{SystemName} Blower',
      position: { x: 700, y: 200 },
    },
    {
      id: 'diffuser',
      componentType: 'diffuser',
      namePattern: '{SystemName} Supply',
      position: { x: 900, y: 200 },
    },
  ],
  connections: [
    { sourceId: 'cooling-coil', sourcePort: 'air-outlet', targetId: 'heating-coil', targetPort: 'air-inlet' },
    { sourceId: 'heating-coil', sourcePort: 'air-outlet', targetId: 'aux-heat', targetPort: 'air-inlet', conditional: "parameters.auxiliaryHeat === true" },
    { sourceId: 'aux-heat', sourcePort: 'air-outlet', targetId: 'fan', targetPort: 'air-inlet', conditional: "parameters.auxiliaryHeat === true" },
    { sourceId: 'heating-coil', sourcePort: 'air-outlet', targetId: 'fan', targetPort: 'air-inlet', conditional: "parameters.auxiliaryHeat === false" },
    { sourceId: 'fan', sourcePort: 'air-outlet', targetId: 'diffuser', targetPort: 'air-inlet' },
  ],
  controls: [],
  plantLoops: [],
  terminalTypes: [],
};

/**
 * Mini-Split Heat Pump Template
 */
const miniSplit: HVACTemplate = {
  id: 'mini-split',
  name: 'Mini-Split Heat Pump',
  description: 'Ductless mini-split heat pump system. Single outdoor unit serving multiple indoor units. Ideal for retrofits and additions.',
  category: 'residential',
  applicability: {
    buildingTypes: ['residential', 'office'],
    climateZones: ['2A', '3A', '4A', '5A', '2B', '3B', '4B', '4C'],
    sizeRange: { min: 20, max: 300 },
  },
  thumbnail: 'mini-split',
  requiresZones: true,
  minZones: 1,
  maxZones: 8,
  parameters: [
    {
      id: 'outdoorCapacity',
      name: 'Outdoor Unit Capacity',
      type: 'select',
      description: 'Total outdoor unit capacity',
      defaultValue: 'autosize',
      options: [
        { label: 'Autosize', value: 'autosize' },
        { label: '9,000 BTU/h (2.6 kW)', value: '2600' },
        { label: '12,000 BTU/h (3.5 kW)', value: '3500' },
        { label: '18,000 BTU/h (5.3 kW)', value: '5300' },
        { label: '24,000 BTU/h (7 kW)', value: '7000' },
        { label: '36,000 BTU/h (10.5 kW)', value: '10500' },
        { label: '48,000 BTU/h (14 kW)', value: '14000' },
      ],
    },
    {
      id: 'inverterDriven',
      name: 'Inverter Driven',
      type: 'boolean',
      description: 'Variable speed compressor',
      defaultValue: true,
    },
    {
      id: 'hyper',
      name: 'Hyper-Heating',
      type: 'boolean',
      description: 'Cold climate heat pump with enhanced low-temp performance',
      defaultValue: false,
    },
  ],
  components: [
    // Mini-splits are modeled through zone equipment, similar to VRF
  ],
  connections: [],
  controls: [],
  plantLoops: [],
  terminalTypes: [
    { componentType: 'mini-split-terminal', hasReheat: false },
  ],
};

/**
 * Water Source Heat Pump Template
 */
const waterSourceHP: HVACTemplate = {
  id: 'water-source-hp',
  name: 'Water Source Heat Pump Loop',
  description: 'Distributed water source heat pumps connected to a common water loop. Simultaneous heating and cooling capability.',
  category: 'advanced',
  applicability: {
    buildingTypes: ['office', 'hotel', 'education'],
    climateZones: ['3A', '4A', '5A', '3B', '4B', '5B'],
    sizeRange: { min: 2000, max: 50000 },
  },
  thumbnail: 'wshp',
  requiresZones: true,
  minZones: 4,
  maxZones: 100,
  parameters: [
    {
      id: 'loopType',
      name: 'Loop Temperature Control',
      type: 'select',
      description: 'How loop temperature is maintained',
      defaultValue: 'boiler-tower',
      options: [
        { label: 'Boiler + Cooling Tower', value: 'boiler-tower' },
        { label: 'Ground Source', value: 'ground' },
        { label: 'Boiler + Fluid Cooler', value: 'boiler-cooler' },
      ],
    },
    {
      id: 'loopTempHigh',
      name: 'Loop High Limit',
      type: 'number',
      description: 'Maximum loop temperature',
      defaultValue: 32,
      unit: '°C',
      validation: { min: 27, max: 38 },
    },
    {
      id: 'loopTempLow',
      name: 'Loop Low Limit',
      type: 'number',
      description: 'Minimum loop temperature',
      defaultValue: 16,
      unit: '°C',
      validation: { min: 10, max: 21 },
    },
    {
      id: 'doas',
      name: 'Include DOAS',
      type: 'boolean',
      description: 'Add dedicated outdoor air system',
      defaultValue: true,
    },
  ],
  components: [
    // Central plant components - boiler and tower
    {
      id: 'doas-erv',
      componentType: 'heat-exchanger-air-to-air',
      namePattern: '{SystemName} DOAS ERV',
      position: { x: 100, y: 100 },
      conditional: "parameters.doas === true",
    },
    {
      id: 'doas-cooling',
      componentType: 'coil-cooling-dx-single-speed',
      namePattern: '{SystemName} DOAS Cooling',
      position: { x: 300, y: 100 },
      conditional: "parameters.doas === true",
    },
    {
      id: 'doas-heating',
      componentType: 'coil-heating-gas',
      namePattern: '{SystemName} DOAS Heating',
      position: { x: 500, y: 100 },
      conditional: "parameters.doas === true",
    },
    {
      id: 'doas-fan',
      componentType: 'fan-constant-volume',
      namePattern: '{SystemName} DOAS Fan',
      position: { x: 700, y: 100 },
      conditional: "parameters.doas === true",
    },
  ],
  connections: [
    { sourceId: 'doas-erv', sourcePort: 'supply-air-outlet', targetId: 'doas-cooling', targetPort: 'air-inlet', conditional: "parameters.doas === true" },
    { sourceId: 'doas-cooling', sourcePort: 'air-outlet', targetId: 'doas-heating', targetPort: 'air-inlet', conditional: "parameters.doas === true" },
    { sourceId: 'doas-heating', sourcePort: 'air-outlet', targetId: 'doas-fan', targetPort: 'air-inlet', conditional: "parameters.doas === true" },
  ],
  controls: [
    { type: 'loop-temperature', parameters: { highLimit: 32, lowLimit: 16 } },
  ],
  plantLoops: [
    {
      type: 'condenser-water',
      equipment: 'cooling-tower',
      sizing: 'auto',
      namePattern: '{SystemName} Heat Pump Loop',
    },
  ],
  terminalTypes: [
    { componentType: 'wshp-terminal', hasReheat: false },
  ],
};

/**
 * All available templates
 */
export const HVAC_TEMPLATES: HVACTemplate[] = [
  vavHotWaterReheat,
  vavElectricReheat,
  vavNoReheat,
  pszAC,
  pvav,
  rooftopGas,
  vrfHeatPump,
  vrfDOAS,
  doasRadiantFloor,
  residentialSplit,
  residentialHeatPump,
  miniSplit,
  waterSourceHP,
];

/**
 * Template lookup by ID
 */
export const TEMPLATE_BY_ID = new Map<string, HVACTemplate>(
  HVAC_TEMPLATES.map((t) => [t.id, t])
);

/**
 * Get template by ID
 */
export function getTemplateById(id: string): HVACTemplate | undefined {
  return TEMPLATE_BY_ID.get(id);
}

/**
 * Get templates by category
 */
export function getTemplatesByCategory(category: TemplateCategory): HVACTemplate[] {
  return HVAC_TEMPLATES.filter((t) => t.category === category);
}

/**
 * Search templates by query
 */
export function searchTemplates(query: string): HVACTemplate[] {
  const lowerQuery = query.toLowerCase();
  return HVAC_TEMPLATES.filter(
    (t) =>
      t.name.toLowerCase().includes(lowerQuery) ||
      t.description.toLowerCase().includes(lowerQuery) ||
      t.category.toLowerCase().includes(lowerQuery)
  );
}

/**
 * Get all template categories with counts
 */
export function getTemplateCategoryCounts(): Map<TemplateCategory, number> {
  const counts = new Map<TemplateCategory, number>();
  for (const template of HVAC_TEMPLATES) {
    counts.set(template.category, (counts.get(template.category) || 0) + 1);
  }
  return counts;
}
